import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { differenceInHours, differenceInSeconds, formatDate } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { BehaviorSubject, first, Subject } from 'rxjs';
import { Tools } from 'src/app/classes/shared/tools';
import { LastAttachmentByDatabase } from 'src/app/interfaces/api/attachment';
import { LastMetricByDatabase } from 'src/app/interfaces/api/metric';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { User } from 'src/app/interfaces/api/user';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, ChipComponent, SpinnerComponent } from 'ui-library';
interface MetricStatus {
  color: "primary" | "danger";
  trackerName: string;
  name: string;
  createdDate: string | number;
}
interface AttachmentStatus {
  color: "primary" | "danger";
  trackerName: string;
  url: string;
  attachment: string;
  createdDate: string | number;
}
interface TrackerStatus {
  color: "primary" | "warning" | "danger";
  config: string;
  online: boolean;
  attachment: boolean;
  shackleBattery: number;
  batteryLevel: number;
  name: string;
  lastUpdate: string | number;
  visible: boolean;
}

interface ProjectStatus {
  status: "primary" | "warning" | "danger";
  database: string;
  name: string;
  trackers: TrackerStatus[];
  attachments: AttachmentStatus[];
  endDate: string | number;
}

@Component({
  selector: 'app-projects-status',
  imports: [
    ChipComponent,
    ButtonComponent,
    CommonModule,
    FormsModule,
    TranslateModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTableModule,
    MatChipsModule,
    MatIconModule,
    MatSortModule,
    MatPaginatorModule,
    MatMenuModule,
    MatButtonToggleModule,
    SpinnerComponent,
    MatToolbarModule,
    TimeFormatPipe,
    ButtonComponent,
    NoDataComponent
  ],
  templateUrl: './projects-status.component.html',
  styleUrl: './projects-status.component.scss'
})

export class ProjectsStatusComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('projectPaginator') projectPaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  user!: User;
  paginator: any = {
    pageIndex: 0,
    pageSize: 10
  };

  paginatorSbj: BehaviorSubject<any> = new BehaviorSubject(this.paginator);
  loading = true;
  projects: Project[] = [];
  projectsCount: number = 0;
  projectsData: MatTableDataSource<ProjectStatus> = new MatTableDataSource();
  projectsColumns: string[] = ['status', 'name', 'trackers', 'metrics', 'attachments', 'endDate'];

  destroy$ = new Subject<void>();
  searchText: string = "";
  validSearchText: string = "";
  trackerConfigs: TrackerConfig[] = [];
  trackers: Tracker[] = [];
  attachments: LastAttachmentByDatabase[] = [];
  metrics: LastMetricByDatabase[] = [];
  configs: TrackerConfig[] = [];
  showProjectWithoutTracker = false;

  constructor(
    private projectService: ProjectService,
    private trackerService: TrackerService,
    private auth: AuthService,
    private attachmentService: AttachmentService,
    private metricService: MetricService,
    private tools: Tools,
  ) {
    this.user = this.auth.currentUser();
  }

  ngOnInit() {
    this.loading = true;
  }

  ngAfterViewInit(): void {

    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(text: string = "") {
    if (text !== this.validSearchText) {
      this.validSearchText = text;
      this.projectPaginator.pageIndex = 0;
      this.loadProjects();
    }
  }

  loadProjects() {

    this.loading = true;

    this.projectService.getProjectListByUser({})
      .pipe(first())
      .subscribe({
        next: (projects) => {
          this.projects = projects;
          this.projectsCount = projects.length;

          this.projects.sort((a, b) => differenceInSeconds(a.createdDate, b.createdDate) < 0 ? 1 : -1);

          if (this.projectPaginator) {
            this.projectPaginator.length = projects.length;
          }

          this.handleProjects();
        },
        error: (err) => {
          this.loading = false;
          console.error('❌ Erreur lors de la recherche des projets:', err);
        }
      });
  }

  getConfigs() {
    this.trackerService.getConfigs().pipe(first()).subscribe({
      next: (res: any) => {
        if (res) this.trackerConfigs = res;
      }
    })
  }

  getTrackers() {
    this.trackerService.getUserTrackers({ limit: 1000 }).subscribe(trackers => {
      this.trackers = trackers;
      this.setMatTableData();
      this.getLastAttachments();
      this.loading = false;
    })
  }

  getLastMetrics() {
    this.metricService.getLastMetricsByDatabase(this.projects.map(p => p.database)).subscribe(lastMetric => {
      this.metrics = lastMetric;
      this.setMatTableData();
    })
  }

  getLastAttachments() {
    this.attachmentService.getLastAttachmentsByDatabase(this.projects.map(p => p.database)).subscribe(lastAttachment => {
      this.attachments = lastAttachment;
      this.getLastMetrics();
      this.setMatTableData();
    })
  }

  filter(configName: string) {
    this.setMatTableData();
  }

  handleProjects() {
    if (this.projects.length === 0) {
      this.setMatTableData();
      this.loading = false;
      return;
    }

    this.projects.sort((a, b) => differenceInSeconds(a.createdDate, b.createdDate) < 0 ? 1 : -1);
    this.getTrackers();
  }

  setMatTableData() {
    let projectStatus: ProjectStatus[] = this.projects.map(p => {
      let trackers = this.trackers.filter(t => (t.project as Project).id == p.id)
      let lastAttachment = this.attachments.find(a => a.database == p.database)
      let lastMetric = this.metrics.find(m => m.database == p.database)
      let statusColor: any = "primary";
      let trackerStatus: TrackerStatus[] = [];
      let attachmentStatus: AttachmentStatus[] = [];
      let metricStatus: MetricStatus[] = [];
      let noSuccess = true;
      if (lastMetric)
        lastMetric.metrics.forEach((metric: any) => {

          let color: any = "primary";
          let diff = differenceInHours(new Date, metric.createdDate);
          if (diff > 1) color = "warning";
          if (diff > 24) color = "danger";
          //retrieve trackerName the sad way
          let status: MetricStatus = {
            color,
            trackerName: metric.tracker,
            name: metric.name,
            createdDate: formatDate(metric.createdDate, "yy-MM-dd HH:mm:ss")

          }
          metricStatus.push(status)
        })
      if (lastAttachment)
        lastAttachment.attachments.forEach((att: any) => {

          let color: any = "primary";
          let diff = differenceInHours(new Date, att.createdDate);
          if (diff > 1) color = "warning";
          if (diff > 24) color = "danger";
          //retrieve trackerName the sad way
          let nameArray = att.name.split('_');
          nameArray.pop();
          nameArray.shift();
          nameArray.shift();
          let status: AttachmentStatus = {
            color,
            trackerName: nameArray.join(' '),
            attachment: att.name,
            url: att.url,
            createdDate: formatDate(att.createdDate, "yy-MM-dd HH:mm:ss")

          }
          attachmentStatus.push(status)
        })
      trackers.forEach(t => {
        let config = t.config as TrackerConfig;
        let currentConfig = this.configs.find(c => c.name == config?.name);
        let visible = false;

        if (!currentConfig) {
          if (config) {
            config.visible = true;
            visible = true;
            this.configs.push(config)
          }
        } else if (config) {
          config.visible = currentConfig.visible;
          visible = config.visible ? true : false;
        } else {
          visible = true;
        }
        let color: any = "primary";
        let isWithinDelay = this.tools.isWithinOfflineDelay(t);
        if ((t.shackleBattery && t.shackleBattery > 0 && t.shackleBattery <= 20) || (t.batteryLevel > 0 && t.batteryLevel <= 20)) {
          color = "warning";
        } else if (!isWithinDelay || t.shackleBattery == 0 || t.batteryLevel == 0) {
          color = "danger";
          statusColor = "warning";
        } else {
          noSuccess = false;
        }
        let status: TrackerStatus = {
          color,
          attachment: false,
          online: isWithinDelay,
          name: t.name,
          config: t.type || "unavailable",
          shackleBattery: t.shackleBattery !== undefined ? t.shackleBattery : -1,
          batteryLevel: t.batteryLevel,
          lastUpdate: t.lastUpdate,
          visible
        };
        trackerStatus.push(status)
      })

      if (noSuccess) {
        statusColor = "danger";
      }
      return {
        status: statusColor,
        name: p.name,
        database: p.database,
        trackers: trackerStatus,
        attachments: attachmentStatus,
        metrics: metricStatus,
        endDate: p.endDate

      }
    });

    if (this.showProjectWithoutTracker) {

      this.projectsData.data = projectStatus;
    } else {

      this.projectsData.data = projectStatus.filter(s => s.trackers.length && s.attachments.length);
    }
  }

  handlePageEvent(e: PageEvent) {
    this.loadProjects();
  }
}
